import React from 'react';

import { ApplicationIdEnum, EmailSenderEnum } from '@search/graphql-typings';
import { RegionIdEnum } from '@search/vtbeco-frontend-core/enums';
import { OfferType } from '@search/vtbeco-frontend-core/domain/offer/search/page/OfferQuery';
import { OfferSnippetModel, sellerCompat } from '@search/vtbeco-frontend-core/domain/offer/search/page/SearchOffersQuery';
import { getAuthorDescription } from '@search/vtbeco-frontend-core/view/common/components/AuthorBadge/AuthorBadge';
import { CallbackModal } from '@search/vtbeco-frontend-core/view/common/components/CallbackModal';
import { Mortgage2StarterButton } from '@search/vtbeco-frontend-core/view/common/components/MinimalBanksMortgage/mortgage2/starter/Mortgage2StarterButton';
import type { LocationPlacementIBType } from '@search/graphql-gateway/server/enums/LocationPlacementIBType';
import type { Mortgage2RealtyType } from '../MinimalBanksMortgage/mortgage2/mortgageTypes';

export function CallToActionModal({
    newBuildingCard,
    offerStatus,
    offerLocation,
    newBuildingEmailExtra,
    seller,
    isMobile,
    isNewBuilding,
    onClose,
    gtmShowModal = offerStatus?.hasAnyPriority ? 'classified_call_back_from_display' : 'popup-flat-call-request',
    gtmSendClick = offerStatus?.hasAnyPriority ? 'classified_call_back_send_form' : 'popup-flat-call-request-send',
    enableInnerText360,
    offerUrl
}: {
    gtmShowModal?: string;
    gtmSendClick?: string;
    onClose?(): void;
    newBuildingCard: OfferType['newBuildingCard'] | OfferSnippetModel['newBuildingCard'];
    offerStatus: OfferType['status'];
    offerLocation: OfferType['location'];
    newBuildingEmailExtra: string;
    seller: ReturnType<typeof sellerCompat>;
    isMobile?: boolean;
    isNewBuilding?: boolean;
    enableInnerText360?: boolean;
    offerUrl?: string;
}) {
    const newBuildingName = newBuildingCard?.title.nominative ?? '';
    const developerName = newBuildingCard?.developerCards[0]?.developer?.title ?? '';
    const newBuildingExtra = `${
        newBuildingName ? `Название объекта: ${newBuildingName}\n` : ''
    }${
        developerName ? `Застройщик: ${developerName}\n` : ''
    }${newBuildingEmailExtra}`;
    const sellerDescr = getAuthorDescription({ category: seller.sellerType, ...seller });

    const vasExtra = `Тип продавца: ${sellerDescr}\n${
        seller.name ? `Данные продавца: ${seller.name}\n` : ''
    }`;

    const address = offerLocation.clickableAddress?.map(({ name }) => name).join(', ') ??
        offerLocation.formattedAddressLong ?? '';

    return (<CallbackModal
        enableInnerText360={enableInnerText360}
        mobile={isMobile}
        withoutIconOnButton={isMobile}
        onClose={onClose}
        developerName={developerName}
        newBuildingName={newBuildingName}
        emailSender={EmailSenderEnum.Search}
        address={address}
        gtmSendClick={gtmSendClick}
        gtmShowModal={gtmShowModal}
        offerLink={offerUrl}
        regionId={offerLocation.narrowRegion?.id ?? RegionIdEnum.MSK}
        newBuildingRanking={newBuildingCard?.ranking}
        isNewBuilding={isNewBuilding}
        // eslint-disable-next-line no-nested-ternary
        extra={isNewBuilding ? newBuildingExtra : offerStatus?.hasAnyPriority ? vasExtra : ''}
        subjectExtra={`${
            isNewBuilding && newBuildingName ? `${newBuildingName} ` : ''
        }${
            offerStatus?.hasAnyPriority ? `VAS, ${sellerDescr}, ${
                seller.name
            }` : ''
        }`}
    />);
}

export function CallToActionButton({
    showCallbackButton,
    locationPlacement,
    newBuildingCard,
    offerStatus,
    offerLocation,
    showMortgageButton,
    newBuildingEmailExtra,
    seller,
    isMobile,
    applicationId,
    isNewBuilding,
    onClose,
    enableInnerText360,
    realtyType
}: Parameters<typeof CallToActionModal>[0] & {
    locationPlacement: LocationPlacementIBType;
    showCallbackButton?: boolean;
    showMortgageButton?: boolean;
    applicationId: ApplicationIdEnum;
    realtyType: Mortgage2RealtyType;
}) {
    return (<>
        {showCallbackButton && (
            <CallToActionModal
                onClose={onClose}
                newBuildingCard={newBuildingCard}
                offerStatus={offerStatus}
                offerLocation={offerLocation}
                newBuildingEmailExtra={newBuildingEmailExtra}
                seller={seller}
                isMobile={isMobile}
                isNewBuilding={isNewBuilding}
                enableInnerText360={enableInnerText360}
            />
        )}
        {showMortgageButton && (
            <Mortgage2StarterButton
                gtmClickId='classified_card_sale_view_ipoteka_call_back'
                locationPlacement={locationPlacement}
                applicationId={applicationId}
                regionId={offerLocation.narrowRegion?.id ?? RegionIdEnum.MSK}
                dataTest='mortgage-button-cta'
                isMobile={isMobile}
                realtyType={realtyType}
                enableInnerText360={enableInnerText360}
            />
        )}
    </>);
}
